import React, { useCallback, useEffect, useState } from 'react';
import { adminService } from '../../services';
import { AdminPageHeader, DataTable, DropChip, DateChip, signedMoney, money, shortDate, useAdminShell } from '../../components/admin/shell';

const TYPES = ['TopUp', 'Commitment', 'CommitmentRefund', 'FlakeFee', 'HostFlakeFeeEarning', 'Bonus', 'Upgrade', 'AdminAdjustment', 'HostEventEarning', 'HostEscrowRelease', 'Withdrawal', 'GuestCheckoutEarning', 'PeerTransferSent', 'PeerTransferReceived',
  // Gathering entry (Oct 2026) — the same ledger, attributed to the parent instead of an event.
  'GatheringWalletReservation', 'GatheringWalletReservationRelease', 'GatheringEscrowCredit', 'GatheringEscrowRelease', 'GatheringRefundReservation', 'GatheringRefundReservationRelease', 'GatheringCardRefund', 'GatheringChargebackDebit'];
const SOURCES = [{ value: '', label: 'ALL' }, { value: 'event', label: 'EVENT' }, { value: 'gathering', label: 'GATHERING' }, { value: 'wallet', label: 'WALLET ONLY' }];

/** MONEY › TRANSACTIONS — spec/sections.md §6. Read-only for everyone. */
export default function TransactionsPage() {
  const { openPerson, canSeePeople } = useAdminShell();
  const [type, setType] = useState('');
  const [source, setSource] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await adminService.getTransactions(page, 30, { type, source, dateFrom: from, dateTo: to });
    setLoading(false);
    if (r.success) { setRows(r.data.transactions || []); setTotal(r.data.total || 0); }
  }, [page, type, source, from, to]);
  useEffect(() => { load(); }, [load]);
  const filtered = type || source || from || to;

  return (
    <>
      <AdminPageHeader section="transactions" meta="Every ledger entry. Filter by type, by source (event · gathering · wallet), by date, then read." />
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <DropChip label="TYPE" value={type} onChange={(v) => { setType(v); setPage(1); }} options={[{ value: '', label: 'ALL' }, ...TYPES.map((t) => ({ value: t, label: t.replace(/([A-Z])/g, ' $1').trim().toUpperCase() }))]} />
          <DropChip label="SOURCE" value={source} onChange={(v) => { setSource(v); setPage(1); }} options={SOURCES} />
          <DateChip label="FROM" value={from} onChange={(v) => { setFrom(v); setPage(1); }} />
          <DateChip label="TO" value={to} onChange={(v) => { setTo(v); setPage(1); }} />
          {filtered && <button type="button" onClick={() => { setType(''); setSource(''); setFrom(''); setTo(''); setPage(1); }} className="font-mono text-[11px] font-bold uppercase tracking-[0.05em] underline">CLEAR</button>}
        </div>
        <DataTable
          columns={[
            { key: 'createdAt', label: 'DATE', fr: 0.9, render: (t) => shortDate(t.createdAt) },
            {
              key: 'userEmail',
              label: 'USER',
              fr: 1.8,
              render: (t) => (
                <span
                  className={canSeePeople && t.userId ? 'cursor-pointer hover:underline' : ''}
                  onClick={canSeePeople && t.userId ? (e) => { e.stopPropagation(); openPerson(t.userId); } : undefined}
                >
                  {t.userEmail || t.userDisplayName || '—'}
                </span>
              ),
            },
            { key: 'type', label: 'TYPE', fr: 1.2 },
            { key: 'amount', label: 'AMOUNT', fr: 0.8, right: true, render: (t) => signedMoney(t.amount) },
            { key: 'balanceAfter', label: 'BALANCE', fr: 0.9, right: true, render: (t) => money(t.balanceAfter) },
            // A gathering's entry row names the gathering; an event row names the event. Never both.
            { key: 'eventTitle', label: 'EVENT / GATHERING', fr: 1.6, render: (t) => (t.gatheringName ? `${t.gatheringName} · GATHERING` : t.eventTitle || '—') },
          ]}
          rows={rows}
          loading={loading}
          total={total}
          page={page}
          pageSize={30}
          onPage={setPage}
        />
      </div>
    </>
  );
}
